import { toast } from "@/components/ui";
import { useHeartbeat } from "@/features/Auth/network/useHeartbeat";
import { useLogout } from "@/features/Auth/network/useLogout";
import { useEnvironment } from "@/hooks/useEnvironment";
import { useUser } from "@/network/useUser";
import type { ReactNode } from "react";
import { useIdleTimer } from "react-idle-timer";

const DEFAULT_IDLE_LOGOUT_TIMEOUT = 1000 * 60 * 60; // 60 minutes
const LOCAL_IDLE_LOGOUT_TIMEOUT = 1000 * 60 * 60 * 12; // 12 hours for local
const DEFAULT_PROMPT_BEFORE_IDLE_TIMEOUT = 1000 * 60 * 30; // 30 minutes
const DEFAULT_IDLE_TOAST_ID = "idle-logout-prompt-toast";

export function IdleTimer({ children }: { children: ReactNode }) {
  const { data: user } = useUser();
  const { mutate: logout } = useLogout();
  const { mutate: heartbeat } = useHeartbeat();
  const { isLocal } = useEnvironment();

  const timeoutDuration = isLocal
    ? LOCAL_IDLE_LOGOUT_TIMEOUT
    : DEFAULT_IDLE_LOGOUT_TIMEOUT;

  const { activate } = useIdleTimer({
    timeout: timeoutDuration,
    promptBeforeIdle: DEFAULT_PROMPT_BEFORE_IDLE_TIMEOUT,
    crossTab: true,
    disabled: !user,
    onIdle: () => {
      logout();
    },
    onAction: () => {
      activate();
      heartbeat();
      toast.dismiss(DEFAULT_IDLE_TOAST_ID);
    },
    onPrompt: () => {
      toast.warning(
        "You've been inactive for a while. You will be logged out soon.",
        {
          duration: timeoutDuration - DEFAULT_PROMPT_BEFORE_IDLE_TIMEOUT,
          id: DEFAULT_IDLE_TOAST_ID,
        },
      );
    },
    throttle: 1000 * 2 * 1,
    events: ["mousemove", "keydown"],
  });

  return children;
}
